import { useEffect, useState } from 'react';
import axios from 'axios';
import i18n from '../i18n/i18n';

export const CURRENCIES = {
  EUR: { code: 'EUR', symbol: '€', name: 'Euro' },
  USD: { code: 'USD', symbol: '$', name: 'US Dollar' },
  GBP: { code: 'GBP', symbol: '£', name: 'British Pound' }
};

export const CURRENCY_LOCALES = {
  EUR: 'nl-NL',
  USD: 'en-US',
  GBP: 'en-GB'
};

export const getDefaultCurrency = (language) => {
  const currencyMap = {
    en: 'USD',
    nl: 'EUR',
    de: 'EUR',
    fr: 'EUR',
    es: 'EUR',
    zh: 'USD'
  };
  return currencyMap[language] || 'EUR';
};

export const UI_LOCALES = {
  nl: 'nl-NL',
  en: 'en-GB',
  fr: 'fr-FR',
  de: 'de-DE',
  es: 'es-ES',
  zh: 'zh-CN'
};

/** Locale van de interfacetaal (valt terug op de valuta-locale). */
export const uiLocale = (currency = 'EUR') => {
  const lang = (i18n.language || 'nl').slice(0, 2);
  return UI_LOCALES[lang] || CURRENCY_LOCALES[currency] || 'en-GB';
};

/** Datum in de taal van de gebruiker. */
export const formatDate = (value, options) => {
  if (!value) return '';
  try {
    return new Date(value).toLocaleDateString(uiLocale(), options);
  } catch (error) {
    return String(value).slice(0, 10);
  }
};

export const formatDateTime = (value) => {
  if (!value) return '';
  try {
    return new Date(value).toLocaleString(uiLocale(), {
      day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit'
    });
  } catch (error) {
    return String(value).slice(0, 16).replace('T', ' ');
  }
};

export const formatCurrency = (amount, currency = 'EUR', language = null) => {
  const locale = language ? (UI_LOCALES[language] || language) : uiLocale(currency);

  try {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  } catch (error) {
    const symbol = CURRENCIES[currency]?.symbol || '€';
    return `${symbol}${(amount || 0).toFixed(2)}`;
  }
};

export const getCurrencySymbol = (currency) => {
  return CURRENCIES[currency]?.symbol || '€';
};

/* ── Wisselkoersen (basis EUR) ───────────────────────────────────────────── */

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const FALLBACK = { base: 'EUR', rates: { EUR: 1, USD: 1.08, GBP: 0.85 }, source: 'fallback' };

let ratesCache = null;
let ratesPromise = null;

export const fetchCurrencyRates = () => {
  if (ratesCache) return Promise.resolve(ratesCache);
  if (!ratesPromise) {
    ratesPromise = axios
      .get(`${BACKEND_URL}/api/currency/rates`)
      .then((res) => {
        ratesCache = res.data && res.data.rates ? res.data : FALLBACK;
        return ratesCache;
      })
      .catch(() => {
        ratesCache = FALLBACK;
        return ratesCache;
      });
  }
  return ratesPromise;
};

export const useCurrencyRates = () => {
  const [rates, setRates] = useState(ratesCache);
  useEffect(() => {
    let mounted = true;
    fetchCurrencyRates().then((r) => {
      if (mounted) setRates(r);
    });
    return () => {
      mounted = false;
    };
  }, []);
  return rates;
};

export const convertFromEUR = (amount, currency = 'EUR', rates = null) => {
  const table = (rates || ratesCache || FALLBACK).rates || {};
  const rate = table[currency] ?? 1;
  return (amount || 0) * rate;
};

/** Bedrag staat in EUR in de database → reken om én formatteer in de doelvaluta. */
export const formatConverted = (amount, currency = 'EUR', rates = null) =>
  formatCurrency(convertFromEUR(amount, currency, rates), currency);
